import { useState, useRef, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

/**
 * هوك لفحص الأسماء المكررة مع debounce
 * @param {string} entityType - 'activist' | 'journalist' | 'institution'
 * @param {number|null} excludeId - ID السجل الحالي (في حالة التعديل)
 */
const useDuplicateCheck = (entityType, excludeId = null) => {
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const debounceTimer = useRef(null);
  const lastCheckedName = useRef('');

  const checkDuplicate = useCallback(async (name) => {
    if (!name || name.trim().length < 3) {
      setDuplicateWarning(null);
      setDuplicateError(null);
      return;
    }

    const trimmed = name.trim();
    // لا نعيد الفحص لنفس الاسم
    if (trimmed === lastCheckedName.current) return;
    lastCheckedName.current = trimmed;

    setCheckingDuplicate(true);
    setDuplicateError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(`${API_BASE_URL}/check-duplicate-name`, {
        name: trimmed,
        type: entityType,
        exclude_id: excludeId,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        timeout: 15000,
      });

      if (res.data && res.data.has_matches) {
        setDuplicateWarning(res.data.matches);
      } else {
        setDuplicateWarning(null);
      }
    } catch (err) {
      console.error('فشل فحص التكرار:', err);
      const status = err?.response?.status;
      if (status === 401) {
        setDuplicateError('الجلسة منتهية - أعد تسجيل الدخول لتفعيل فحص التكرار');
      } else if (status >= 500) {
        setDuplicateError('خطأ في الخادم أثناء فحص التكرار');
      } else if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
        setDuplicateError('مشكلة في الاتصال - تعذّر فحص التكرار');
      } else {
        setDuplicateError(`خطأ في فحص التكرار: ${status || err.message}`);
      }
      setDuplicateWarning(null);
    } finally {
      setCheckingDuplicate(false);
    }
  }, [entityType, excludeId]);

  const handleNameBlur = useCallback((e) => {
    const name = e.target.value;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    lastCheckedName.current = ''; // فرض إعادة الفحص عند فقدان التركيز
    checkDuplicate(name);
  }, [checkDuplicate]);

  const handleNameChange = useCallback((name) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      checkDuplicate(name);
    }, 1000);
  }, [checkDuplicate]);

  const dismissWarning = useCallback(() => {
    setDuplicateWarning(null);
    setDuplicateError(null);
  }, []);

  // فحص عند الحفظ - يرجع true إذا فيه تكرارات
  const checkBeforeSave = useCallback(async (name) => {
    if (!name || name.trim().length < 3) return false;
    
    const trimmed = name.trim();
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(`${API_BASE_URL}/check-duplicate-name`, {
        name: trimmed,
        type: entityType,
        exclude_id: excludeId,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        timeout: 15000,
      });

      if (res.data && res.data.has_matches) {
        setDuplicateWarning(res.data.matches);
        return res.data.matches;
      }
    } catch (err) {
      console.error('فشل فحص التكرار عند الحفظ:', err);
    }
    return false;
  }, [entityType, excludeId]);

  return {
    duplicateWarning,
    checkingDuplicate,
    duplicateError,
    handleNameBlur,
    handleNameChange,
    dismissWarning,
    checkBeforeSave,
  };
};

export default useDuplicateCheck;
